<template>
    <div class="withdraws">
        <form @submit.prevent="getData">
            <custom-select
                v-if="isSupervisor"
                v-model="requestParams.WebShopId"
                :title="$t('WITHDRAWS__WEBSHOP')"
                :options="webshopOptions"
                @clear="requestParams.WebShopId = ''"
            />
            <three-way-checkbox
                v-model="requestParams.TransactionStatus"
                :title="$t('WITHDRAWS__TRANSACTION_STATUS')"
            />
            <custom-input
                :title="$t('WITHDRAWS__USERNAME')"
                v-model="requestParams.UserName"
                @clear="requestParams.UserName = ''"
            />
            <custom-input
                :title="$t('WITHDRAWS__AMOUNT_FROM')"
                v-model="requestParams.AmountFromDecimal"
                @clear="requestParams.AmountFromDecimal = ''"
            />
            <custom-input
                :title="$t('WITHDRAWS__AMOUNT_TO')"
                v-model="requestParams.AmountToDecimal"
                @clear="requestParams.AmountToDecimal = ''"
            />
            <date-picker
                :title="$t('WITHDRAWS__START_DATE')"
                v-model="requestParams.CreateTimeFrom"
                @clear="requestParams.CreateTimeFrom = ''"
            />
            <date-picker
                :title="$t('WITHDRAWS__TO_DATE')"
                v-model="requestParams.CreateTimeTo"
                @clear="requestParams.CreateTimeTo = ''"
            />
            <button class="btn">{{ $t('WITHDRAWS__SEARCH') }}</button>
        </form>
        <div class="loader" v-show="loading"></div>
        <grid
            v-show="!loading"
            :fullGrid="true"
            :passedData="gridData"
            identificator="transactionId"
            :columns="columns"
            :passedCount="dataCount"
            :passedPageSize="15"
            :title="$t('WITHDRAWS__WITHDRAWS')"
            @pageChanged="pageChangeHandler"
            @trClicked="trClickHandler"
            @sort="sortHandler"
            :lastRow="lastRow"
        />
        <custom-snackbar
            :purpose="mainSnackbar.purpose"
            :text="mainSnackbar.text"
            v-if="mainSnackbar.visible"
            @close="mainSnackbar.visible = false"
        />
    </div>
</template>

<script>
import axios from '@/Helpers/AxiosInstance.js'
import Grid from '@/components/ReusableComponents/Grid/Grid.vue'
import { addHours, subtractHours } from '@/Helpers/HelperFunctions.js'
import CustomSnackbar from '@/components/ReusableComponents/CustomSnackbar/CustomSnackbar.vue'
import ThreeWayCheckbox from '@/components/ReusableComponents/ThreeWayCheckbox/ThreeWayCheckbox.vue'
import CustomSelect from '@/components/ReusableComponents/CustomSelect/CustomSelect.vue'
import CustomInput from '@/components/ReusableComponents/CustomInput/CustomInput.vue'
import DatePicker from '@/components/ReusableComponents/DatePicker/DatePicker.vue'
import EventBus from '@/Helpers/EventBus.js'
export default {
    components: {
        Grid,
        CustomSnackbar,
        CustomSelect,
        CustomInput,
        ThreeWayCheckbox,
        DatePicker,
    },
    created() {
        if(this.isSupervisor) {
            this.requestParams.OrderColumn = 'startDate'
            this.requestParams.OrderDirection = 'desc'
        }
        else {
            this.requestParams.OrderColumn = 'completedInWebShop'
            this.requestParams.OrderDirection = 'asc'
            this.requestParams.WebShopId = this.$store.state.webshops[0].id
        }
        this.getData()
    },
    data() {
        return {
            gridData: [],
            requestParams: {
                WebShopId: '',
                TransactionStatus: false,
                CreateTimeFrom: '',
                CreateTimeTo: '',
                AmountFromDecimal: '',
                AmountToDecimal: '',
                UserName: '',
                OrderColumn: '',
                OrderDirection: '',
                CurrentPage: 1,
                PageSize: 15,
            },
            dataCount: 0,
            totalAmount: '',
            mainSnackbar: {
                visible: false,
                text: '',
                purpose: 'success',
            },
            loading: false,
        }
    },
    computed: {
        columns() {
            var columns = [
                {
                    title: this.$t('WITHDRAWS__TRANSACTION_ID'),
                    key: 'transactionId',
                },
                {
                    title: this.$t('WITHDRAWS__WEB_SHOP'),
                    key: 'webShopName',
                },
                {
                    title: this.$t('WITHDRAWS__USERNAME'),
                    key: 'userName',
                },
                {
                    title: this.$t('WITHDRAWS__START_DATE'),
                    key: 'startDate',
                    customTemplate: (item, val) =>
                        addHours(val, 2),
                    
                },
                {
                    title: this.$t('WITHDRAWS__AMOUNT'),
                    key: 'amount',
                    customTemplate: (item, val) =>
                        val + ' ' + item.currencyName,
                },
            ]
            if (!this.isSupervisor) {
                columns.push({
                    title: 'Confirm',
                    key: 'completedInWebShop',
                    customTemplate: (item, val) =>
                        !val
                            ? `<div class="confirm-container">
                            <input type="text" placeholder="Code" class="general-input">
                            <button class="btn">${this.$t('WITHDRAWS__CONFIRM')}</button>
                            </div`
                            : '',
                    tdClass: 'td-confirm',
                })
            } else {
                columns.push({
                    title: 'Code',
                    key: 'withdrawCode',
                })
            }
            return columns
        },
        isSupervisor() {
            return this.$store.state.shop
        },
        webshopOptions() {
            var options = []
            this.$store.state.webshops.forEach(shop => {
                let title = shop.name
                    ? `${shop.calculatedName} (${shop.name})`
                    : shop.calculatedName
                options.push({ title: title, value: shop.id + '' })
            })
            return options
        },
        statusCodes() {
            return this.$store.state.statusCodes
        },
        lastRow() {
            if(this.dataCount == 0) return []
            if(this.isSupervisor) {
                return ['Total', '', '', '', this.totalAmount, '']
            }
            return ['Total', '', '', '', this.totalAmount, '', '']
        },
    },
    methods: {
        getData() {
            this.loading = true
            let copy = {...this.requestParams}
            copy.CreateTimeFrom = subtractHours(this.requestParams.CreateTimeFrom, 2)
            var timeTo = new Date(this.requestParams.CreateTimeTo)
            timeTo.setDate(timeTo.getDate() + 1) // add one day
            copy.CreateTimeTo = this.requestParams.CreateTimeTo ? subtractHours(timeTo, 2) : ''
            axios
                .get('WebShop/GetWebShopWithdraws', {
                    params: { ...copy },
                })
                .then(resp => {
                    this.gridData = resp.data.response
                    this.dataCount =
                        resp.data.response.length > 0
                            ? resp.data.response[0].count
                            : 0
                    this.totalAmount = 
                        resp.data.response.length > 0
                            ? resp.data.response[0].totalAmount + ' ' +  resp.data.response[0].currencyName
                            : 0
                    this.loading = false
                })
        },
        pageChangeHandler(page) {
            this.requestParams.CurrentPage = page
            this.getData()
        },
        trClickHandler(element, e) {
            if (e.target.matches('.btn')) {
                var input = e.target.previousElementSibling
                var code = input.value
                if (code == '') return
                axios
                    .post('WebShop/ConfirmWebShopWithdraw', {
                        transactionId: element.transactionId,
                        code: code,
                    })
                    .then(resp => {
                        if (resp.data.status == 1) {
                            this.mainSnackbar.text = 'Confirm Is Successful!'
                            this.mainSnackbar.purpose = 'success'
                            this.mainSnackbar.visible = 'true'
                            input.value = ''
                            this.getData()
                            EventBus.$emit('updateBalance')
                        } else {
                            this.showConfirmError(resp.data.status)
                        }
                    })
                    .catch(() => {
                        this.showConfirmError()
                    })
            }
        },
        showConfirmError(status) {
            this.mainSnackbar.text = 'An Error Occurred While Confirming'
            this.mainSnackbar.purpose = 'error'
            this.mainSnackbar.visible = 'true'
            var code = this.statusCodes.filter(e => e.key == status)[0]
            if (code) this.mainSnackbar.text = this.$t('STATUS-CODE__' + code.value)
        },
        sortHandler(data) {
            this.requestParams.OrderDirection = data.orderByDesc ? 'desc' : 'asc'
            this.requestParams.OrderColumn = data.columnName
            this.getData()
        }
    },
}
</script>

<style lang="scss" scoped>
/deep/ .confirm-container {
    height: 40 * $rem;

    .btn {
        margin-left: 15 * $rem;
    }
}

/deep/ .td-confirm {
    max-width: initial !important;
}

form {
    display: flex;
    flex-wrap: wrap;
    margin-bottom: 15 * $rem;
    margin-top: -25 * $rem;

    label,
    .select-wrapper,
    .checkbox-wrapper,
    .switcher-container,
    .btn {
        margin-right: 15 * $rem;
        margin-top: 25 * $rem;
        width: calc(100% / 7 - #{15 * $rem});
    }
}

@media only screen and (max-width: 1920px) {
    form { 
        label,
        .select-wrapper,
        .checkbox-wrapper,
        .switcher-container,
        .btn {
            margin-right: 15 * $rem;
            margin-top: 25 * $rem;
            width: calc(100% / 5 - #{15 * $rem});
        }

        .btn {
            margin-left: auto;
        }
    }
}

@media only screen and (max-width: 1600px) {
    form { 
        label,
        .select-wrapper,
        .checkbox-wrapper,
        .switcher-container,
        .btn {
            margin-right: 15 * $rem;
            margin-top: 25 * $rem;
            width: calc(100% / 4 - #{15 * $rem});
        }
    }
}

@media only screen and (max-width: 800px) {
    form { 
        label,
        .select-wrapper,
        .checkbox-wrapper,
        .switcher-container,
        .btn {
            margin-right: 15 * $rem;
            margin-top: 25 * $rem;
            width: calc(100% / 2 - #{15 * $rem});
        }
    }
}

@media only screen and (max-width: 320px) {
    form { 
        label,
        .select-wrapper,
        .checkbox-wrapper,
        .switcher-container,
        .btn {
            margin-right: 15 * $rem;
            margin-top: 25 * $rem;
            width: calc(100% - #{15 * $rem});
        }
    }
}
</style>